import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { EGInput } from "../../../../MainComponents/Input/EGInput";
import { InvestigationButton, Separator } from "../components";
import Comments from "../../Information/Comments";

/** Caracteres a partir de los cuales la ficha corta el texto con "leer más" */
const PREVIEW_LENGTH = 500;

/** Tope de captura, para que el resumen siga siendo un resumen */
export const SUMMARY_MAX_LENGTH = 4000;

/**
 * Acepta lo que venga del API y siempre devuelve `{ text }`.
 *
 * Se guarda como objeto y no como string suelto porque el cifrado del folder
 * (`encrypt_dict`) solo recorre dict y list: un string no se cifraría. El caso
 * string se contempla por si quedara alguna carpeta guardada antes.
 */
export const normalizeReportSummary = (value) => {
  if (typeof value === "string") return { text: value };
  if (value && typeof value === "object") {
    return { text: typeof value.text === "string" ? value.text : "" };
  }
  return { text: "" };
};

/**
 * Resumen de la denuncia dentro de la carpeta de investigación: lo redacta
 * quien investiga para no tener que releer la narrativa completa cada vez.
 *
 * Vive en `folder.report_summary` y se ve como ficha y se edita en el mismo
 * lugar, igual que la clasificación del caso.
 */
export const ReportSummary = ({
  investigation,
  onSave,
  justView = false,
}) => {
  const [t] = useTranslation("investigation");
  const theme = useTheme();

  const saved = useMemo(
    () => normalizeReportSummary(investigation?.report_summary),
    [investigation?.report_summary]
  );

  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [text, setText] = useState(saved.text);

  useEffect(() => {
    setText(saved.text);
  }, [saved]);

  const trimmed = text.trim();
  const isEmpty = saved.text.trim().length === 0;
  const hasChanges = trimmed !== saved.text.trim();

  const handleSave = async () => {
    setIsLoading(true);
    try {
      await onSave({ text: trimmed });
      setIsEditing(false);
    } catch (error) {
      console.error(error);
      enqueueSnackbar({
        variant: "error",
        message: t("reportSummary.save_error"),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setText(saved.text);
    setIsEditing(false);
  };

  const renderView = () => {
    if (isEmpty) {
      return (
        <Typography fontSize={14} color={theme.palette.blueGray500.main} mt={1}>
          {t("reportSummary.empty")}
        </Typography>
      );
    }

    // Mismo tratamiento que la descripción del reporte: respeta los saltos de
    // línea y corta con "leer más" cuando el resumen es largo.
    return (
      <Comments
        padding={0}
        maxLength={PREVIEW_LENGTH}
        text={saved.text}
        color="secondary"
        fontSize={14}
      />
    );
  };

  const renderForm = () => (
    <Stack spacing={1.5} mt={1.5}>
      <EGInput
        label={t("reportSummary.label")}
        value={text}
        onChange={(e) => setText(e.target.value.slice(0, SUMMARY_MAX_LENGTH))}
        multiline
        rows={6}
        showHelpText={false}
        placeholder={t("reportSummary.placeholder")}
      />

      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "center" }}
        spacing={1}
      >
        <Typography fontSize={12} color={theme.palette.blueGray500.main}>
          {t("reportSummary.counter", {
            count: text.length,
            max: SUMMARY_MAX_LENGTH,
          })}
        </Typography>

        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="flex-end"
          spacing={1}
        >
          <InvestigationButton
            label={t("reportSummary.save")}
            variant="success"
            size="medium"
            icon="mark_check_bold"
            onClick={handleSave}
            loading={isLoading}
            disabled={!hasChanges}
          />
          <InvestigationButton
            label={t("reportSummary.cancel")}
            icon="close_icon"
            size="medium"
            onClick={handleCancel}
            loading={isLoading}
          />
        </Stack>
      </Stack>
    </Stack>
  );

  return (
    <Box
      className="rounded dyTheme2 pt-[10] pe-[20] pb-[16] ps-[20]"
      mt={1.7}
      marginBottom={2}
      sx={{ border: `1px solid ${theme.palette.primary.main}` }}
    >
      <Separator
        text={t("reportSummary.title")}
        icon="bold_document_text"
        iconButton={!justView && !isEditing ? "outline_edit" : undefined}
        onClick={!justView && !isEditing ? () => setIsEditing(true) : undefined}
      />

      {isEditing ? renderForm() : renderView()}
    </Box>
  );
};
